import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];
  /** @type {number} */
  #prevWidth = 0;
  /** @type {number} */
  #prevCopies = 0;
  /** @type {number} */
  #prevSpeed = 0;

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();
    // Initialize previous metrics to avoid unnecessary restarts on mobile scroll-induced resizes
    this.#prevWidth = this.offsetWidth;
    this.#prevCopies = this.#calculateNumberOfCopies();
    this.#prevSpeed = this.#calculateSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
    // Touch press-and-hold: pause on down, resume on up/cancel
    this.addEventListener('pointerdown', this.#handlePointerDown, { passive: true });
    this.addEventListener('pointerup', this.#handlePointerUpOrCancel, { passive: true });
    this.addEventListener('pointercancel', this.#handlePointerUpOrCancel, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointerup', this.#handlePointerUpOrCancel);
    this.removeEventListener('pointercancel', this.#handlePointerUpOrCancel);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * Safely update animation playback rate with WAAPI fallback
   * @param {Animation} animation
   * @param {number} value
   */
  #setPlaybackRate(animation, value) {
    if (!animation) return;
    if (typeof animation.updatePlaybackRate === 'function') {
      animation.updatePlaybackRate(value);
      return;
    }

    try {
      // Fallback for browsers without updatePlaybackRate
      animation.playbackRate = value;
    } catch (_) {}
  }

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => this.#setPlaybackRate(animation, value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => this.#setPlaybackRate(animation, value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  // Pause immediately without resetting currentTime (for touch press-hold)
  #pauseAnimationImmediate() {
    this.#slowDown.cancel();
    this.#animation?.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    try {
      animation.pause();
    } catch (_) {}
  }

  // Resume immediately and ensure normal speed (for touch release)
  #resumeAnimationImmediate() {
    this.#slowDown.cancel();
    this.#animation?.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#setPlaybackRate(animation, 1);
    try {
      animation.play();
    } catch (_) {}
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    // Only react to touch pointers to avoid interfering with mouse
    if (event.pointerType !== 'touch') return;
    // For touch, pause immediately without resetting the timeline
    this.#pauseAnimationImmediate();
  };

  /** @param {PointerEvent} event */
  #handlePointerUpOrCancel = (event) => {
    if (event.pointerType !== 'touch') return;
    // Resume and ensure speed is normal
    this.#resumeAnimationImmediate();
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;

    // Only react when width meaningfully changes to avoid mobile scroll resize jitter
    const newWidth = this.offsetWidth;
    const widthDelta = Math.abs(newWidth - this.#prevWidth);

    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;
    const newSpeed = this.#calculateSpeed();

    let didChangeStructure = false;
    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
      didChangeStructure = true;
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
      didChangeStructure = true;
    }

    const didSpeedChange = newSpeed !== this.#prevSpeed;
    const didWidthChange = widthDelta > 1; // ignore sub-pixel jitter

    // Update metrics
    this.#prevWidth = newWidth;
    this.#prevCopies = newNumberOfCopies;
    this.#prevSpeed = newSpeed;

    if (didChangeStructure) {
      this.#duplicateContent();
    }

    if (didSpeedChange) {
      this.#setSpeed(newSpeed);
    }

    // Only restart when structure changed or width meaningfully changed; avoid on height-only changes
    if (didChangeStructure || didWidthChange) {
      this.#restartAnimation();
    }
  }, 200);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
